import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowBigDown,
  ArrowRight,
  Award,
  Bandage,
  BedDouble,
  Bone,
  ChevronRight,
  CircleDot,
  Clock,
  Droplets,
  Dumbbell,
  GraduationCap,
  Layers,
  Maximize2,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  ScanFace,
  Shuffle,
  Sparkles,
} from "lucide-react";


const Breadcrumb = () => {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Chin Augmentation", href: "/chin-augmentation-in-mumbai" },
  ];

  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

const HeroSection = () => (
  <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
            Chin Augmentation
          </h1>
          <div className="w-12 h-[2px] bg-gold mb-5" />
          <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
            Chin Implant &amp; Genioplasty Surgery by Dr. Vicky Jain
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-certified">Board Certified</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-experience">17+ Years Experience</span>
            <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-training">International Training</span>
          </div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <ProcedurePlaceholder procedureName="Chin Augmentation" icon="◇" size="hero"  imageSrc={HERO.chinAugmentation} imageAlt="Chin augmentation" />
        </div>
      </div>
    </div>
  </section>
);

const WhatIsSection = () => (
  <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Chin Augmentation Surgery?</h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Chin augmentation improves the size, projection, and contour of the chin to create a balanced facial profile. Most patients focus on their nose, lips, or skin. The chin is the feature they overlook, and often the one doing the most damage to their side profile.
          </p>
          <p>
            A recessed or weak chin makes the nose appear larger than it is, reduces jawline definition, and creates the illusion of a double chin even when no excess fat is present. Dr. Vicky Jain performs chin augmentation using implants, sliding genioplasty, or non-surgical fillers depending on what your anatomy requires.
          </p>
          <p>
            A significant number of patients who initially consult for rhinoplasty benefit more from chin augmentation, or a combination of both. Correcting chin projection changes how the entire lower face reads without touching anything else.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const DrJainApproachSection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
            Dr. Jain&apos;s Approach to Chin Augmentation
          </h2>
          <div className="space-y-6 text-white/75 leading-relaxed">
            <p>
              Dr. Jain evaluates the chin in relation to the nose, lips, and jawline, not in isolation. The nose-to-chin ratio and side profile balance determine what correction is needed and how much. Implant size and positioning are selected based on your facial proportions, not a standard template.
            </p>
            <p>
              The goal is a chin that looks like it has always been yours. Overdone results come from ignoring facial context. Dr. Jain&apos;s approach keeps the enhancement in proportion with the rest of your face so the improvement reads as natural to everyone who knows you.
            </p>
          </div>
        </div>
        <div className="relative">
          <div className="relative overflow-hidden">
            <img
              src="/dr-jain.avif"
              alt="Dr. Vicky Jain"
              loading="lazy"
              decoding="async"
              className="w-full h-[420px] object-cover object-top"
              data-testid="dr-jain-photo"
            />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{
                background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)",
              }}
            />
          </div>
          <div className="mt-4 text-center lg:text-left">
            <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ChinProblemsSection = () => {
  const cards = [
    {
      title: "Weak or Recessed Chin",
      icon: ArrowBigDown,
      copy: "A chin that sits behind the vertical line of the lips and nose disrupts the entire facial profile. Augmentation brings it into proportion.",
    },
    {
      title: "Poor Jawline Definition",
      icon: Layers,
      copy: "A projected chin anchors the jawline. Without it, the jaw looks soft and undefined even on a lean face.",
    },
    {
      title: "Double Chin Illusion",
      icon: ScanFace,
      copy: "A recessed chin creates a visual drop from the lip to the neck that reads as a double chin. Structural correction eliminates this without fat removal.",
    },
    {
      title: "Flat Side Profile",
      icon: Maximize2,
      copy: "The side profile is where chin deficiency is most visible. Even modest forward projection changes how the face reads from the side.",
    },
    {
      title: "Facial Asymmetry",
      icon: Shuffle,
      copy: "Chin deviation or asymmetry creates imbalance across the lower face. Implants or genioplasty can correct this depending on the degree of asymmetry.",
    },
    {
      title: "Post-Injury Deformity",
      icon: Bandage,
      copy: "Trauma to the chin or jaw can leave structural deformity. Dr. Jain assesses whether implant placement or bone repositioning is the appropriate correction.",
    },
  ];

  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="chin-problems-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          What Chin Augmentation Corrects
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`chin-problem-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProcedureTypesSection = () => {
  const types = [
    {
      title: "Chin Implants",
      icon: CircleDot,
      copy: "The most commonly performed technique. A medical-grade silicone implant is placed through a small incision under the chin or inside the mouth. Results are permanent, predictable, and carry a short recovery period. No visible external scar in most cases.",
    },
    {
      title: "Sliding Genioplasty",
      icon: Bone,
      copy: "The chin bone is surgically repositioned and fixed with titanium plates. Used for significant chin deficiency, skeletal correction, or cases where an implant is not appropriate. Uses your own anatomy, no foreign material required.",
    },
    {
      title: "Non-Surgical Chin Augmentation",
      icon: Droplets,
      copy: "Dermal fillers add projection and shape without surgery. Results last 9 to 12 months. Best for patients with mild concerns or those who want to assess the change before committing to surgery. Fillers cannot correct structural deficiency or asymmetry permanently.",
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="procedure-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">
          Types of Chin Augmentation Surgery
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {types.map((item, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`procedure-type-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <item.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowSurgeryPerformedSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
            How Chin Augmentation Surgery is Performed
          </h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Surgery takes 45 to 90 minutes under local or general anesthesia. Dr. Jain makes a small incision either inside the mouth along the lower gum line or in the natural crease beneath the chin. A pocket is created over the chin bone, the implant is positioned, and the incision is closed.
          </p>
          <p>
            There are no visible external scars in most cases. The implant sits directly on the bone, which keeps it stable. Displacement is rare when placement is precise.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const RecoveryTimelineSection = () => {
  const milestones = [
    {
      period: "Days 1 to 3",
      description: "Swelling and tightness around the chin and jaw. Normal and expected.",
      icon: BedDouble,
    },
    {
      period: "Days 7 to 10",
      description: "Most patients return to desk work. Visible swelling reduces significantly.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 3",
      description: "Swelling continues to settle. The chin starts to look and feel more natural.",
      icon: Dumbbell,
    },
    {
      period: "1 to 3 Months",
      description: "Final shape is visible. The implant integrates with surrounding tissue and feels like part of the face.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhoIsGoodCandidateSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-good-candidate-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
            Who Is a Good Candidate for Chin Augmentation?
          </h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Good candidates are adults who have finished facial growth, are in good general health, and have a specific structural concern with their chin or lower face profile. You do not need to have an extreme deficiency to benefit. Many patients have subtle recessions that create disproportionate effects on their overall profile.
          </p>
          <p>
            Non-smokers have better healing outcomes. Patients with realistic expectations, meaning those who want improvement in their own face rather than a specific appearance, get the best results. If your side profile bothers you or your jawline reads as soft and undefined, a consultation with Dr. Jain will establish whether augmentation addresses the root cause.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PatientResultsSection = () => <PatientTestimonials procedureName="Chin Augmentation" />;

const FAQSection = () => {
  const faqs = [
    {
      question: "What is the difference between chin implants and sliding genioplasty?",
      answer:
        "Chin implants add projection using a silicone implant placed over the chin bone. Sliding genioplasty physically repositions the chin bone itself. Dr. Jain recommends genioplasty for significant skeletal deficiency and implants for moderate projection concerns.",
    },
    {
      question: "Is chin augmentation painful?",
      answer:
        "Surgery is performed under anesthesia, so there is no pain during the procedure. Post-operative discomfort is mild and managed with standard medication. Most patients describe tightness more than pain.",
    },
    {
      question: "Will chin augmentation look natural?",
      answer:
        "Yes, when implant size and position are matched to your facial proportions. An implant that is too large for the face is the most common cause of unnatural results.",
    },
    {
      question: "Can chin augmentation be combined with rhinoplasty?",
      answer:
        "Yes, and this combination is common. Improving both chin projection and nasal shape together produces facial balance that neither procedure achieves alone.",
    },
    {
      question: "How long does recovery take?",
      answer: "Most patients return to work in 7 to 10 days. Final results are visible in 1 to 3 months as swelling fully resolves.",
    },
    {
      question: "Are results permanent?",
      answer:
        "Yes for implant-based augmentation. Silicone implants are stable and long-lasting. Non-surgical filler results last 9 to 12 months.",
    },
    {
      question: "Will there be visible scarring?",
      answer:
        "In most cases, no. The incision is placed inside the mouth or in the natural crease under the chin where it is not visible.",
    },
    {
      question: "Can fillers replace surgery permanently?",
      answer:
        "No. Fillers add temporary volume and work for mild cases or as a trial. They cannot correct structural deficiency, skeletal asymmetry, or significant chin recession permanently.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-b border-deep-navy/10"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

const ConsultationCTASection = () => (
  <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
        Book Your Chin Augmentation Consultation
      </h2>
      <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
        During your consultation, Dr. Jain evaluates your chin projection, jawline structure, and nose-to-chin ratio to determine what correction produces the best result for your face. You leave with a clear surgical plan, realistic expectations, and a full understanding of what recovery involves.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <Button
          asChild
          className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
          data-testid="cta-book-consultation"
        >
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            Book Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
        <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
      </div>
    </div>
  </section>
);

const RelatedProceduresSection = () => {
  const procedures = [
    {
      name: "Rhinoplasty",
      description: "Balance nose and chin projection together for complete facial harmony.",
      href: "/rhinoplasty-in-mumbai",
    },
    {
      name: "Facelift",
      description: "Jawline tightening and lower face rejuvenation.",
      href: "/facelift-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Double chin fat removal to complement structural chin correction.",
      href: "/liposuction-surgery-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
              data-testid={`related-procedure-${index}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <a
                href={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const chinBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://drvickyjain.com/" },
    { "@type": "ListItem", position: 2, name: "Cosmetic Surgery", item: "https://drvickyjain.com/cosmetic-surgery-in-mumbai" },
    { "@type": "ListItem", position: 3, name: "Chin Augmentation", item: "https://drvickyjain.com/chin-augmentation-in-mumbai" },
  ],
};

const chinStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Chin Augmentation",
      alternateName: ["Chin implant surgery", "Genioplasty", "Mentoplasty"],
      description: "Surgical enhancement of chin projection and definition using silicone implants or sliding genioplasty to improve facial balance and profile.",
      procedureType: "Surgical",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Chin" },
      followup: "Days 1–7: mild swelling and tightness. Week 1–2: return to desk work. Month 1–3: final results visible as swelling resolves.",
      howPerformed: "General or local anesthesia; incision inside mouth or under chin; implant placed over chin bone or bone repositioned (genioplasty); 45 to 90 minutes; same-day discharge.",
      url: "https://drvickyjain.com/chin-augmentation-in-mumbai",
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "What is the difference between chin implants and sliding genioplasty?", acceptedAnswer: { "@type": "Answer", text: "Chin implants add projection using a silicone implant placed over the chin bone. Sliding genioplasty physically repositions the chin bone itself. Dr. Jain recommends genioplasty for significant skeletal deficiency and implants for moderate projection concerns." } },
        { "@type": "Question", name: "Is chin augmentation painful?", acceptedAnswer: { "@type": "Answer", text: "Surgery is performed under anesthesia, so there is no pain during the procedure. Post-operative discomfort is mild and managed with standard medication. Most patients describe tightness more than pain." } },
        { "@type": "Question", name: "Will chin augmentation look natural?", acceptedAnswer: { "@type": "Answer", text: "Yes, when implant size and position are matched to your facial proportions. An implant that is too large for the face is the most common cause of unnatural results." } },
        { "@type": "Question", name: "Can chin augmentation be combined with rhinoplasty?", acceptedAnswer: { "@type": "Answer", text: "Yes, and this combination is common. Improving both chin projection and nasal shape together produces facial balance that neither procedure achieves alone." } },
        { "@type": "Question", name: "How long does recovery take?", acceptedAnswer: { "@type": "Answer", text: "Most patients return to work in 7 to 10 days. Final results are visible in 1 to 3 months as swelling fully resolves." } },
        { "@type": "Question", name: "Are results permanent?", acceptedAnswer: { "@type": "Answer", text: "Yes for implant-based augmentation. Silicone implants are stable and long-lasting. Non-surgical filler results last 9 to 12 months." } },
        { "@type": "Question", name: "Will there be visible scarring?", acceptedAnswer: { "@type": "Answer", text: "In most cases, no. The incision is placed inside the mouth or in the natural crease under the chin where it is not visible." } },
        { "@type": "Question", name: "Can fillers replace surgery permanently?", acceptedAnswer: { "@type": "Answer", text: "No. Fillers add temporary volume and work for mild cases or as a trial. They cannot correct structural deficiency, skeletal asymmetry, or significant chin recession permanently." } },
      ],
    },
  ],
};

const ChinAugmentationPage = () => (
  <>
    <SEO
      title="Chin Augmentation | Dr. Vicky Jain"
      description="A weak chin affects your entire profile. Dr. Vicky Jain performs chin augmentation using implants or genioplasty. Book your consultation today."
      canonical="/chin-augmentation-in-mumbai"
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(chinBreadcrumb) }} />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(chinStructuredData) }} />
    <Breadcrumb />
    <HeroSection />
    <WhatIsSection />
    <DrJainApproachSection />
    <ChinProblemsSection />
    <ProcedureTypesSection />
    <HowSurgeryPerformedSection />
    <RecoveryTimelineSection />
    <WhoIsGoodCandidateSection />
    <PatientResultsSection />
    <FAQSection />
    <ConsultationCTASection />
    <RelatedProceduresSection />
  </>
);

export default ChinAugmentationPage;
